import {
  useBoundProp,
  useCardSession,
  useCardStatus,
  useCardSessionControls,
  type ComponentFn,
} from '@littlebird/action-cards-view/react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { emptyPlaceholder, StackedField } from '../../field';
import { fieldErrorId, useElementId, useFieldChecks } from '../../fieldIds';
import { isTone, sizingClass, TEXT_STYLE, TONE_CLASS } from '../../tokens';

import { LengthCounter } from './LengthCounter';
import { MarkdownEditor } from './MarkdownEditor';
import { PlainEditor, type EditorProps } from './PlainEditor';
import { ReadOnlyValue } from './ReadOnlyValue';

import type { catalog } from '@littlebird/action-cards-view';

export const TextArea: ComponentFn<typeof catalog, 'TextArea'> = ({
  props,
  bindings,
}) => {
  const id = useElementId(props);
  const [opened, setOpened] = useState(false);
  const [value, setValue] = useBoundProp<string>(props.value, bindings?.value);
  const checks = useFieldChecks({
    path: bindings?.value,
    checks: props.checks,
    validateOn: props.validateOn,
    serverError: props.error,
  });
  const session = useCardSession();
  const status = useCardStatus();
  const controls = useCardSessionControls();
  const path = bindings?.value;
  // An add line opens the field by focusing its path before the editor exists
  const open = opened || (path !== undefined && session.focused === path);
  const held = status === 'running';
  // A literal value has no state path to write to, so it shows as text
  const editable =
    path !== undefined &&
    props.editable !== false &&
    !session.readOnly &&
    !held;
  const error = checks.error ?? props.error;
  const appearance = props.appearance ?? 'inline';
  const textStyle = props.textStyle ?? 'body';
  const format = props.format ?? 'plain';
  // Not a catalog prop: a component composing TextArea without a visible label
  // passes the field's name
  const ariaLabel =
    !props.label && typeof props.ariaLabel === 'string'
      ? props.ariaLabel
      : undefined;

  const editorProps: EditorProps = {
    id,
    value: value ?? '',
    placeholder: emptyPlaceholder(props.placeholder, props.label, 'Add text'),
    error,
    appearance,
    outline: props.outline,
    textStyle,
    lines: props.lines,
    toneClass: isTone(props.tone)
      ? TONE_CLASS[props.tone]
      : textStyle === 'note'
        ? TONE_CLASS.subtle
        : undefined,
    bodyClass:
      typeof props.bodyClass === 'string' ? props.bodyClass : undefined,
    ariaLabel: props.label ?? ariaLabel,
    autoFocus: open,
    onChange: (next) => {
      setValue(next);
      checks.afterChange();
    },
    onFocus: () => {
      setOpened(true);
      if (path) controls.focus(path);
    },
    onBlur: () => {
      setOpened(false);
      checks.onBlur();
      if (path) controls.blur(path);
    },
  };

  if (props.hideWhenEmpty && !value?.trim() && !open) return null;

  let control;
  if (!editable) {
    control = (
      <ReadOnlyValue
        id={id}
        label={props.label}
        value={value ?? ''}
        placeholder={props.placeholder}
        format={format}
        lines={props.lines}
        textStyle={textStyle}
        held={held}
        toneClass={editorProps.toneClass}
        bodyClass={editorProps.bodyClass}
      />
    );
  } else if (
    !value?.trim() &&
    !open &&
    !props.checks?.some((check) => check.type === 'required')
  ) {
    control = (
      <button
        id={id}
        type="button"
        aria-describedby={error ? fieldErrorId(id) : undefined}
        title={error}
        className={cn(
          'text-left',
          TEXT_STYLE.note,
          error
            ? TONE_CLASS.danger
            : 'text-foreground-muted hover:text-foreground'
        )}
        onClick={() => setOpened(true)}
      >
        {props.label ?? ariaLabel ?? props.placeholder ?? 'Add text'}
      </button>
    );
  } else if (format === 'markdown' || format === 'mrkdwn') {
    control = <MarkdownEditor {...editorProps} format={format} />;
  } else {
    control = <PlainEditor {...editorProps} />;
  }

  const max = props.checks?.find((check) => check.type === 'maxLength')?.args
    ?.max;
  const limit = typeof max === 'number' ? max : undefined;
  const field = (
    <StackedField
      id={id}
      label={props.label}
      labelsControl={editable}
      error={error}
      labelHidden={props.labelHidden}
    >
      <div className="relative">
        {control}
        {editable && limit !== undefined ? (
          <LengthCounter length={(value ?? '').length} limit={limit} />
        ) : null}
      </div>
    </StackedField>
  );

  return props.sizing === 'fill' ? (
    <div className={sizingClass(props.sizing)}>{field}</div>
  ) : (
    field
  );
};
